import { AppleButton } from '@iheartjane/menu';
import type { Nullable } from '@iheartjane/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';

import { useHardRedirect } from '@jane/bloom/hooks/useHardRedirect';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { AppleIdTokensResponse } from '@jane/bloom/routes/api.auth.id-tokens.apple/types';
import {
  trackLoggedIn,
  trackRegistered,
} from '@jane/bloom/services/tracking/track';
import { logError } from '@jane/bloom/utils/logError';

/** Apple raises these "errors" when the user dismisses or restarts the popup. */
const ACCEPTABLE_ERROR_CODES = [
  'user_cancelled_authorize',
  'popup_closed_by_user',
  'popup_blocked_by_browser',
  'user_trigger_new_signin_flow',
];

interface AppleSignInButtonProps {
  appleSsoClientId?: Nullable<string>;
  /**
   * When provided, called on successful Apple auth instead of a hard redirect,
   * keeping the user on the page (loaders still revalidate). Mirrors `useLogin`.
   */
  onSuccess?: (result: { redirectTo: string }) => void;
  skipBagAfterAuth?: boolean;
}

export const AppleSignInButton = ({
  appleSsoClientId,
  onSuccess,
  skipBagAfterAuth,
}: AppleSignInButtonProps) => {
  const { isReady, signIn } = useAppleSignIn({
    appleSsoClientId,
    onSuccess,
    skipBagAfterAuth,
  });

  return <AppleButton disabled={!isReady} onClick={signIn} />;
};

/**
 * Initializes the Sign In With Apple JS client with the partner's own
 * Services ID (client id) and exchanges the resulting identity token
 * through the bloom server.
 * Requires the external script to load on the page - https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js
 * @see https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js
 */
const useAppleSignIn = ({
  appleSsoClientId,
  onSuccess,
  skipBagAfterAuth,
}: AppleSignInButtonProps) => {
  const [isReady, setIsReady] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { featureFlags, googleSsoClientId, menuUrl } = useRootLoaderData();
  const fetcher = useFetcher<AppleIdTokensResponse>();
  const hardRedirect = useHardRedirect();

  const submitToken = useCallback(
    async (response: AppleSignInAPI.SignInResponseI) => {
      fetcher.submit(
        {
          first_name: response.user?.name?.firstName ?? '',
          id_token: response.authorization.id_token,
          last_name: response.user?.name?.lastName ?? '',
          skipBag: `${skipBagAfterAuth}`,
        },
        {
          action: `/api/auth/id-tokens/apple`,
          method: 'POST',
        }
      );
    },
    [fetcher, skipBagAfterAuth]
  );

  useEffect(() => {
    if (fetcher.state !== 'idle' || !fetcher.data) return;

    if ('error' in fetcher.data) {
      logError(new Error(`Apple sign-in failed: ${fetcher.data.error}`));
      return;
    }

    const { new_registration } = fetcher.data.user;

    if (new_registration) {
      trackRegistered(
        {
          googleSsoEnabled: !!googleSsoClientId,
          method: 'apple',
          source: fetcher.data.authSource,
        },
        featureFlags
      );
    } else {
      trackLoggedIn(
        {
          googleSsoEnabled: !!googleSsoClientId,
          method: 'apple',
          source: fetcher.data.authSource,
        },
        featureFlags
      );
    }

    if (onSuccess) {
      onSuccess({ redirectTo: fetcher.data.redirectTo });
    } else {
      hardRedirect(fetcher.data.redirectTo);
    }
  }, [
    featureFlags,
    fetcher.data,
    fetcher.state,
    googleSsoClientId,
    hardRedirect,
    onSuccess,
  ]);

  useEffect(() => {
    if (!appleSsoClientId) return;

    const waitForAppleApi = (): Promise<void> => {
      return new Promise((resolve) => {
        const checkAppleApi = () => {
          if (window.AppleID?.auth) {
            timeoutRef.current = null;
            resolve();
          } else {
            timeoutRef.current = setTimeout(checkAppleApi, 100);
          }
        };
        checkAppleApi();
      });
    };

    const initializeAppleSignIn = async () => {
      try {
        await waitForAppleApi();

        window.AppleID?.auth.init({
          clientId: appleSsoClientId,
          // Must exactly match a Return URL registered on the partner's
          // Services ID; with usePopup it is never actually navigated to.
          redirectURI: `${menuUrl}/api/auth/id-tokens/apple`,
          scope: 'name email',
          usePopup: true,
        });

        setIsReady(true);
      } catch (error) {
        logError(error);
      }
    };

    initializeAppleSignIn();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [appleSsoClientId, menuUrl]);

  const signIn = useCallback(async () => {
    try {
      const response = await window.AppleID?.auth.signIn();
      if (response) {
        await submitToken(response);
      }
    } catch (error) {
      const errorCode = (error as AppleSignInAPI.SignInErrorI)?.error;
      if (!ACCEPTABLE_ERROR_CODES.includes(errorCode)) {
        logError(error);
      }
    }
  }, [submitToken]);

  return { isReady, signIn };
};
